import { useState } from "react";
import axios from "axios";
import { MapPin, PhoneCall, WhatsappLogo } from "@phosphor-icons/react";
import { toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { contactInfo, services } from "@/data/siteContent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
  full_name: "",
  phone: "",
  email: "",
  origin: "",
  destination: "",
  service_type: "Evden Eve Nakliyat",
  message: "",
};

export default function ContactSection() {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);

  const updateField = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form, email: form.email || null, message: form.message || null };
      await axios.post(`${API}/quotes`, payload);
      toast.success("Teklif talebiniz alındı", { description: "Ekibimiz en kısa sürede sizi arayacak." });
      setForm(initialForm);
    } catch (error) {
      toast.error("Talep gönderilemedi", { description: "Lütfen bilgileri kontrol edip tekrar deneyin veya doğrudan arayın." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="iletisim" data-testid="contact-section" className="relative bg-[#0A0A0B] px-6 py-24 lg:px-8 lg:py-32">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(249,115,22,0.16),transparent_32%),radial-gradient(circle_at_80%_70%,rgba(245,158,11,0.10),transparent_30%)]" />
      <div className="relative mx-auto grid max-w-7xl gap-8 lg:grid-cols-[0.9fr_1.1fr]">
        <div data-testid="contact-info-panel" className="rounded-3xl border border-white/10 bg-[#121214] p-7 shadow-2xl md:p-10">
          <p data-testid="contact-overline" className="text-xs font-black uppercase tracking-[0.22em] text-orange-500">Hızlı İletişim</p>
          <h2 data-testid="contact-title" className="mt-4 font-heading text-3xl font-black tracking-tight text-white sm:text-4xl">Taşınma planınızı bugün netleştirelim.</h2>
          <p data-testid="contact-description" className="mt-5 text-base leading-relaxed text-slate-400">Nereden nereye taşınacağınızı yazın; ekip, araç ve zaman planını size uygun şekilde oluşturalım.</p>

          <div data-testid="direct-contact-list" data-stagger-group="contact-info-items" className="mt-9 space-y-4">
            <a data-stagger-item="contact-info-item" data-testid="contact-phone-link" href={contactInfo.phoneHref} className="flex items-center gap-4 rounded-2xl border border-white/10 bg-black/30 p-4 transition-[border-color,transform] duration-300 hover:-translate-y-1 hover:border-orange-500/60">
              <PhoneCall size={28} weight="duotone" className="text-orange-500" />
              <span>
                <span data-testid="contact-phone-label" className="block text-xs font-black uppercase tracking-widest text-slate-500">Telefon</span>
                <span data-testid="contact-phone-value" className="font-heading text-lg font-black text-white">{contactInfo.phoneDisplay}</span>
              </span>
            </a>
            <a
              data-testid="contact-whatsapp-link"
              data-stagger-item="contact-info-item"
              href={contactInfo.whatsappHref}
              target={contactInfo.whatsappHref.startsWith("http") ? "_blank" : undefined}
              rel={contactInfo.whatsappHref.startsWith("http") ? "noreferrer" : undefined}
              className="flex items-center gap-4 rounded-2xl border border-white/10 bg-black/30 p-4 transition-[border-color,transform] duration-300 hover:-translate-y-1 hover:border-[#25D366]/70"
            >
              <WhatsappLogo size={28} weight="duotone" className="text-[#25D366]" />
              <span data-testid="contact-whatsapp-text" className="font-semibold text-slate-200">WhatsApp: {contactInfo.phoneDisplay}</span>
            </a>
            <div data-stagger-item="contact-info-item" data-testid="contact-address-row" className="flex items-center gap-4 rounded-2xl border border-white/10 bg-black/30 p-4">
              <MapPin size={28} weight="duotone" className="text-orange-500" />
              <span>
                <span data-testid="contact-address-label" className="block text-xs font-black uppercase tracking-widest text-slate-500">Adres</span>
                <span data-testid="contact-address-value" className="font-semibold text-slate-200">{contactInfo.publicLocationLabel}</span>
              </span>
            </div>
          </div>
        </div>

        <form data-testid="quote-form" onSubmit={handleSubmit} className="rounded-3xl border border-white/15 bg-[#1A1A1E] p-6 shadow-2xl md:p-8">
          <div data-stagger-group="quote-form-fields" className="grid gap-5 md:grid-cols-2">
            <label data-stagger-item="quote-form-field" data-testid="quote-name-field" className="space-y-2 text-sm font-bold text-slate-300">
              Ad Soyad
              <Input data-testid="quote-name-input" required value={form.full_name} onChange={(e) => updateField("full_name", e.target.value)} placeholder="Adınız soyadınız" className="h-12 border-white/15 bg-black/40 text-white placeholder:text-slate-600 focus-visible:ring-orange-500" />
            </label>
            <label data-stagger-item="quote-form-field" data-testid="quote-phone-field" className="space-y-2 text-sm font-bold text-slate-300">
              Telefon
              <Input data-testid="quote-phone-input" required value={form.phone} onChange={(e) => updateField("phone", e.target.value)} placeholder="05xx xxx xx xx" className="h-12 border-white/15 bg-black/40 text-white placeholder:text-slate-600 focus-visible:ring-orange-500" />
            </label>
            <label data-stagger-item="quote-form-field" data-testid="quote-email-field" className="space-y-2 text-sm font-bold text-slate-300">
              E-posta
              <Input data-testid="quote-email-input" type="email" value={form.email} onChange={(e) => updateField("email", e.target.value)} placeholder="ornek@mail.com" className="h-12 border-white/15 bg-black/40 text-white placeholder:text-slate-600 focus-visible:ring-orange-500" />
            </label>
            <label data-stagger-item="quote-form-field" data-testid="quote-service-field" className="space-y-2 text-sm font-bold text-slate-300">
              Hizmet Türü
              <select data-testid="quote-service-select" value={form.service_type} onChange={(e) => updateField("service_type", e.target.value)} className="h-12 w-full rounded-md border border-white/15 bg-black/40 px-3 text-sm text-white outline-none transition-colors duration-300 focus:border-orange-500 focus:ring-1 focus:ring-orange-500">
                {services.map((service) => <option key={service.id} value={service.title}>{service.title}</option>)}
              </select>
            </label>
            <label data-stagger-item="quote-form-field" data-testid="quote-origin-field" className="space-y-2 text-sm font-bold text-slate-300">
              Nereden
              <Input data-testid="quote-origin-input" required value={form.origin} onChange={(e) => updateField("origin", e.target.value)} placeholder="Kırıkhan / Hatay" className="h-12 border-white/15 bg-black/40 text-white placeholder:text-slate-600 focus-visible:ring-orange-500" />
            </label>
            <label data-stagger-item="quote-form-field" data-testid="quote-destination-field" className="space-y-2 text-sm font-bold text-slate-300">
              Nereye
              <Input data-testid="quote-destination-input" required value={form.destination} onChange={(e) => updateField("destination", e.target.value)} placeholder="Yeni adres / şehir" className="h-12 border-white/15 bg-black/40 text-white placeholder:text-slate-600 focus-visible:ring-orange-500" />
            </label>
          </div>
          <label data-testid="quote-message-field" className="mt-5 block space-y-2 text-sm font-bold text-slate-300">
            Mesajınız
            <Textarea data-testid="quote-message-input" value={form.message} onChange={(e) => updateField("message", e.target.value)} placeholder="Kat bilgisi, eşya yoğunluğu veya özel notlar..." className="min-h-32 border-white/15 bg-black/40 text-white placeholder:text-slate-600 focus-visible:ring-orange-500" />
          </label>
          <Button data-testid="quote-submit-button" disabled={loading} className="mt-6 h-14 w-full rounded-xl bg-orange-500 text-base font-black text-white transition-[transform,box-shadow,background-color] duration-300 hover:-translate-y-1 hover:bg-orange-600 hover:shadow-[0_8px_30px_rgba(249,115,22,0.3)]">
            {loading ? "Gönderiliyor..." : "Hemen Fiyat Al"}
          </Button>
          <p data-testid="quote-form-helper" className="mt-4 text-center text-xs text-slate-500">Bilgileriniz yalnızca teklif süreci için kullanılır.</p>
        </form>
      </div>
    </section>
  );
}